import React from "react";
import { MessageSquare, X, Minus, Maximize2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import Chatbot from "./ChatBot";

/**
 * Floating assistant, bottom-right, available from any screen.
 *
 * The chatbot previously lived only on its own page, so using it meant leaving
 * whatever you were looking at. This keeps it one click away without taking over
 * the view.
 *
 * The panel stays mounted once opened (hidden with CSS rather than unmounted) so
 * the conversation survives closing and reopening — unmounting would reset the
 * message history every time.
 */
export default function FloatingChat() {
  const [open, setOpen] = React.useState(false);
  const [everOpened, setEverOpened] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);

  const openPanel = () => { setOpen(true); setEverOpened(true); };

  // Escape closes the panel — expected of any overlay.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const width = expanded ? "min(900px, calc(100vw - 48px))" : "min(440px, calc(100vw - 48px))";
  const height = expanded ? "min(820px, calc(100vh - 96px))" : "min(620px, calc(100vh - 120px))";

  return (
    <>
      {/* Launcher — hidden while the panel is open so the two never overlap. */}
      {!open && (
        <button
          onClick={openPanel}
          aria-label="Open the AI assistant"
          style={{
            position: "fixed", right: 24, bottom: 24, zIndex: 1000,
            display: "flex", alignItems: "center", gap: 10,
            padding: "14px 20px", borderRadius: 999, border: "none", cursor: "pointer",
            background: "linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-teal) 100%)",
            color: "var(--cares-text-max)", fontSize: 14, fontWeight: 600,
            boxShadow: "0 10px 25px rgba(79,70,229,0.35)",
            transition: "transform .15s ease, box-shadow .15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 14px 30px rgba(79,70,229,0.45)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "0 10px 25px rgba(79,70,229,0.35)";
          }}
        >
          <MessageSquare size={18} />
          Ask AIEA
        </button>
      )}

      {/* Rendered from first open onward and hidden with display:none, so the
          conversation is still there when it is reopened. */}
      {everOpened && (
        <div
          style={{
            position: "fixed", right: 24, bottom: 24, zIndex: 1000,
            width, height, display: open ? "flex" : "none", flexDirection: "column",
            background: "var(--cares-surface-card)", borderRadius: 16, overflow: "hidden",
            border: "1px solid var(--cares-line-card)",
            boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
            background: "linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-teal) 100%)", color: "var(--cares-text-max)",
          }}>
            <MessageSquare size={16} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>AIEA Assistant</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>
                Risk · pipeline · process · portfolio
              </div>
            </div>
            <button onClick={() => setExpanded((v) => !v)} title={expanded ? "Shrink" : "Expand"}
                    style={iconBtn}>
              {expanded ? <Minus size={15} /> : <Maximize2 size={15} />}
            </button>
            <button onClick={() => setOpen(false)} title="Close" style={iconBtn}>
              <X size={16} />
            </button>
          </div>

          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Chatbot compact />
          </div>
        </div>
      )}
    </>
  );
}

const iconBtn = {
  background: "rgba(255,255,255,0.18)", border: "none", color: "var(--cares-text-max)",
  borderRadius: 8, width: 28, height: 28, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
};


/**
 * Mounts the launcher on every authenticated screen, but not on the login page —
 * an assistant floating over a sign-in form is noise, and nothing it can answer is
 * available before you are logged in.
 */
export function FloatingChatGate() {
  const { pathname } = useLocation();
  if (/\/login\/?$/.test(pathname)) return null;
  return <FloatingChat />;
}
